import { useCurrentAccount, useResolveAccessRequest } from '@gripp/shared-logic';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';
import { Button } from '../crop-circle/Button';
import { tokens } from '../crop-circle/tokens';
import { messageStyles } from './styles';
import { MessageComponent, MessageComponentProps } from './types';

export const AccessRequestMessage: MessageComponent = (
  props: MessageComponentProps
) => {
  const { resolveAccessRequest, isLoading } = useResolveAccessRequest();
  const { account } = useCurrentAccount();
  const { t } = useTranslation();
  const actionData = props.message.actionData;
  // Only users who can resolve access requests (matches the resolveAccessRequest
  // resolver gate) get the Approve/Deny actions. The requester sees the same
  // ticket, read-only.
  const canManageAccess = account?.hasPermission('account.changeRole') ?? false;
  const showButtons = canManageAccess && actionData?.status !== 'APPROVED';

  const handleApprove = async () => {
    try {
      await resolveAccessRequest({
        issueId: props.issue.id,
        decision: 'APPROVED',
      });
    } catch (err) {
      console.error('Failed to approve access request:', err);
    }
  };

  const handleDeny = async () => {
    try {
      await resolveAccessRequest({
        issueId: props.issue.id,
        decision: 'DENIED',
      });
    } catch (err) {
      console.error('Failed to deny access request:', err);
    }
  };

  const requestText = props.message.text?.trim();
  const description = requestText
    ? t('issue.accessRequest.workspaceAccessRequest', { message: requestText })
    : t('issue.accessRequest.requestingFullAccess');

  return (
    <View>
      <Text style={[messageStyles.messageText, props.textStyle]}>
        {description}
      </Text>
      {showButtons && (
        <View style={styles.buttonRow}>
          <Button
            variant="rectangular"
            state="default"
            size="large"
            disabled={isLoading}
            accessibilityLabel={t('issue.accessRequest.approveA11y')}
            onPress={handleApprove}
            style={styles.button}
          >
            {t('issue.accessRequest.approve')}
          </Button>
          <Button
            variant="rectangular"
            state="warning"
            size="large"
            disabled={isLoading}
            accessibilityLabel={t('issue.accessRequest.denyA11y')}
            onPress={handleDeny}
            style={styles.button}
          >
            {t('issue.accessRequest.deny')}
          </Button>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  buttonRow: {
    flexDirection: 'row',
    gap: tokens.spacing.sm,
    marginTop: tokens.spacing.md,
  },
  button: {
    flex: 1,
  },
});
